import { useEffect, useState } from "react";
import { axiosInstance } from "@/lib/axios";

/**
 * Renders an authenticated image by fetching it as a blob via the axios
 * instance (which attaches the JWT). Handles LOTO gridfs IDs, generic
 * stored photo paths (object storage / gridfs://... / legacy uploads/...),
 * and arbitrary custom axios URLs.
 *
 * Usage:
 *   <AuthedImage gridfsId="abc123" />                                    (LOTO)
 *   <AuthedImage storedPath="safetymindai/audit_photos/xyz.jpg" />       (any audit/incident photo)
 *   <AuthedImage url="/custom/endpoint/12" />                            (arbitrary)
 */
export default function AuthedImage({
  gridfsId,
  storedPath,
  url,
  alt = "",
  className = "",
  "data-testid": dataTestId,
}) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl = null;

    // Resolve which endpoint to call.
    let axiosUrl = null;
    let axiosParams = undefined;
    if (gridfsId) {
      axiosUrl = `/loto/photo/${gridfsId}`;
    } else if (storedPath) {
      axiosUrl = `/stored-photo`;
      axiosParams = { path: storedPath };
    } else if (url) {
      axiosUrl = url;
    }

    if (!axiosUrl) {
      setSrc(null);
      return () => {};
    }

    setFailed(false);
    axiosInstance
      .get(axiosUrl, { responseType: "blob", params: axiosParams })
      .then(({ data }) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(data);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!revoked) setFailed(true);
      });
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [gridfsId, storedPath, url]);

  if (failed) {
    return (
      <div
        className={`bg-gray-100 border border-dashed border-gray-300 rounded flex items-center justify-center text-[10px] text-gray-500 ${className}`}
        style={{ minWidth: 60, minHeight: 60 }}
        data-testid={dataTestId}
      >
        Photo unavailable
      </div>
    );
  }
  if (!src) {
    return (
      <div
        className={`bg-gray-100 animate-pulse rounded ${className}`}
        style={{ minWidth: 60, minHeight: 60 }}
        data-testid={dataTestId}
      />
    );
  }
  return <img src={src} alt={alt} className={className} data-testid={dataTestId} draggable={false} />;
}
